import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Package, CheckCircle2, AlertTriangle, Siren,
  PhoneOff, Hourglass, CalendarCheck, FilePlus, Plus, Building2,
  Clock, TrendingUp,
} from "lucide-react";
import { useApp, usePerm } from "@/context/AppContext";
import { dashboardStats, dashboardByLocation } from "@/lib/api";

// ----------- KPI cards (unchanged content, compact styling) ---------------
const CARDS = [
  { key: "new_orders",              label: "New Orders",              icon: ClipboardList,  color: "blue",    filter: "new_order" },
  { key: "drafts",                  label: "Draft Orders",            icon: FilePlus,       color: "amber",   filter: "draft",   to: "orders" },
  { key: "ordered",                 label: "Ordered",                 icon: Package,        color: "amber",   filter: "ordered" },
  { key: "pending_pickup",          label: "Pending Pickup",          icon: CheckCircle2,   color: "emerald", filter: "pending_pickup" },
  { key: "delayed",                 label: "Delayed",                 icon: AlertTriangle,  color: "rose",    filter: "delayed" },
  { key: "urgent",                  label: "Urgent Orders",           icon: Siren,          color: "red",     filter: "urgent" },
  { key: "customer_not_responding", label: "Customer Not Responding", icon: PhoneOff,       color: "purple",  filter: "customer_not_responding" },
  { key: "sla_breached",            label: "SLA Breached",            icon: Hourglass,      color: "orange",  filter: "sla_breached" },
  { key: "due_today",               label: "Due Today",               icon: CalendarCheck,  color: "indigo",  filter: "due_today" },
];

const ICON_BG = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-600",    bar: "bg-blue-500"    },
  amber:   { bg: "bg-amber-50",   text: "text-amber-600",   bar: "bg-amber-500"   },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600", bar: "bg-emerald-500" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-600",    bar: "bg-rose-500"    },
  red:     { bg: "bg-red-50",     text: "text-red-600",     bar: "bg-red-500"     },
  purple:  { bg: "bg-purple-50",  text: "text-purple-600",  bar: "bg-purple-500"  },
  orange:  { bg: "bg-orange-50",  text: "text-orange-600",  bar: "bg-orange-500"  },
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-600",  bar: "bg-indigo-500"  },
};

// ------------------------------ Helpers ------------------------------------
// Colour-code a numeric cell inside the branch performance table.
const NUM_TONE = {
  delayed:       { hot: "text-rose-600 font-bold",   warm: "text-amber-600 font-semibold", cold: "text-[#9ca0ad]" },
  sla_breached:  { hot: "text-rose-600 font-bold",   warm: "text-amber-600 font-semibold", cold: "text-[#9ca0ad]" },
  pending_pickup:{ hot: "text-emerald-700 font-bold", warm: "text-emerald-600 font-semibold", cold: "text-[#9ca0ad]" },
  customer_not_responding:{ hot: "text-purple-600 font-bold", warm: "text-purple-500 font-semibold", cold: "text-[#9ca0ad]" },
  new_orders:    { hot: "text-blue-700 font-bold",    warm: "text-blue-600 font-semibold",  cold: "text-[#9ca0ad]" },
  ordered:       { hot: "text-amber-700 font-bold",   warm: "text-amber-600 font-semibold", cold: "text-[#9ca0ad]" },
};

const num = (v, metric) => {
  const n = Number(v) || 0;
  const tone = NUM_TONE[metric] || NUM_TONE.new_orders;
  if (n === 0) return { cls: tone.cold, txt: "0" };
  // Simple thresholds — 3+ hot, else warm
  return { cls: n >= 3 ? tone.hot : tone.warm, txt: String(n) };
};

const HEALTH_STYLE = {
  red:   { dot: "bg-rose-500",   label: "Critical", ring: "border-rose-200 bg-rose-50" },
  amber: { dot: "bg-amber-500",  label: "Watch",    ring: "border-amber-200 bg-amber-50" },
  green: { dot: "bg-emerald-500",label: "Healthy",  ring: "border-emerald-200 bg-emerald-50" },
};

// Filter → workbench tab mapping (mirrors CARDS `filter` + a few extras)
const WORKBENCH_FILTER = {
  new_orders: "new_order",
  ordered: "ordered",
  pending_pickup: "pending_pickup",
  delayed: "delayed",
  sla_breached: "sla_breached",
  customer_not_responding: "customer_not_responding",
};

// ----------------------------- Component -----------------------------------
const Dashboard = () => {
  const { currentLocation, locations } = useApp();
  const canAddSpares = usePerm("spares", "add");
  const navigate = useNavigate();

  const [stats, setStats] = useState({});
  const [byLoc, setByLoc] = useState({ rows: [], oldest_pending_pickup: null, oldest_delayed: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, b] = await Promise.all([
          dashboardStats(currentLocation),
          dashboardByLocation(),
        ]);
        setStats(s);
        setByLoc(b);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentLocation]);

  // Deep-link into Workbench with location + status pre-filtered.
  const jumpTo = (locCode, metric) => {
    const wbTab = WORKBENCH_FILTER[metric];
    if (!wbTab) return;
    navigate(`/workbench?filter=${wbTab}&location=${locCode}`);
  };

  // ----- Rollups for "Today's Attention" (top 4 by severity) --------------
  const attention = useMemo(() => {
    const rows = byLoc.rows || [];
    // Score each location: delayed + sla have highest weights, cnr medium,
    // pending_pickup only shown if >5. Ordered locations that need a look.
    const scored = rows.map((r) => {
      const score =
        (r.delayed || 0) * 4 +
        (r.sla_breached || 0) * 3 +
        (r.customer_not_responding || 0) * 2 +
        Math.max(0, (r.pending_pickup || 0) - 5);
      // Build a short reason string: e.g. "5 Delayed · 4 SLA"
      const parts = [];
      if (r.delayed) parts.push(`${r.delayed} Delayed`);
      if (r.sla_breached) parts.push(`${r.sla_breached} SLA`);
      if (r.customer_not_responding) parts.push(`${r.customer_not_responding} CNR`);
      if (!parts.length && r.pending_pickup > 5) parts.push(`${r.pending_pickup} Pending Pickup`);
      return { ...r, score, reason: parts.slice(0, 2).join(" · ") };
    });
    // Sort by score DESC, take top 4. Then a "healthy" tail slot for green.
    scored.sort((a, b) => b.score - a.score);
    return scored;
  }, [byLoc]);

  const totalRow = useMemo(() => {
    const t = { new_orders: 0, ordered: 0, pending_pickup: 0, delayed: 0, customer_not_responding: 0, sla_breached: 0 };
    (byLoc.rows || []).forEach((r) => {
      Object.keys(t).forEach((k) => { t[k] += r[k] || 0; });
    });
    return t;
  }, [byLoc]);

  const locationName = (code) => {
    const l = (locations || []).find((x) => x.code === code);
    return l?.name || code;
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-6 pb-12" data-testid="dashboard-page">
      {/* Header */}
      <header className="pt-2 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="text-[28px] font-semibold text-yamaha-800 tracking-tight leading-none">
            Operational Dashboard
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1.5">
            Real-time overview of order status and branch performance.
          </p>
        </div>
        {canAddSpares && (
        <button
          data-testid="header-create-btn"
          onClick={() => navigate("/create")}
          className="bg-yamaha-700 hover:bg-yamaha-800 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 text-[13px] font-semibold shadow-sm"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          Create Parts Request
        </button>
        )}
      </header>

      {/* --- 1) KPI cards (unchanged) + Today's Summary --------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {CARDS.map((c) => {
          const value = stats[c.key] ?? 0;
          const palette = ICON_BG[c.color];
          const onClickCard = () => {
            if (c.to === "orders") navigate(`/orders?tab=${c.filter}`);
            else navigate(`/workbench?filter=${c.filter}`);
          };
          return (
            <button
              key={c.key}
              data-testid={`dash-card-${c.key}`}
              onClick={onClickCard}
              className="group relative bg-white rounded-xl border border-[#ebebf2] text-left overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:shadow-[0_6px_16px_rgba(16,24,40,0.06)] hover:-translate-y-0.5 transition-all"
            >
              <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${palette.bar}`}></span>
              <div className="p-4 flex flex-col">
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${palette.bg} ${palette.text}`}>
                  <c.icon className="w-4 h-4" strokeWidth={2.4} />
                </div>
                <div className="mt-3 text-[26px] font-semibold text-[#0c0d12] leading-none tracking-tight">
                  {value}
                </div>
                <div className="text-[12px] font-semibold text-[#1a1b22] mt-1.5 leading-tight">{c.label}</div>
                <div className="text-[10.5px] text-yamaha-700 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  View details →
                </div>
              </div>
            </button>
          );
        })}
        {/* Today's Summary — the 10th tile */}
        <div
          data-testid="dash-todays-summary"
          className="bg-white rounded-xl border border-[#ebebf2] p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md flex items-center justify-center bg-yamaha-50 text-yamaha-700">
              <TrendingUp className="w-4 h-4" strokeWidth={2.4} />
            </div>
            <div className="text-[12px] font-semibold text-[#1a1b22]">Today's Summary</div>
          </div>
          <ul className="mt-3 space-y-1 text-[11.5px] text-[#444653]">
            <li className="flex justify-between"><span>New Orders</span><b>{stats.new_orders ?? 0}</b></li>
            <li className="flex justify-between"><span>Drafts Pending</span><b>{stats.drafts ?? 0}</b></li>
            <li className="flex justify-between"><span>Delayed</span><b className="text-rose-600">{stats.delayed ?? 0}</b></li>
            <li className="flex justify-between"><span>Awaiting Pickup</span><b className="text-emerald-700">{stats.pending_pickup ?? 0}</b></li>
            <li className="flex justify-between"><span>SLA Breached</span><b className="text-orange-600">{stats.sla_breached ?? 0}</b></li>
          </ul>
        </div>
      </div>

      {/* --- 2) Branch Performance table + Today's Attention ----------------- */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Location Performance */}
        <div
          data-testid="branch-performance"
          className="lg:col-span-2 bg-white rounded-xl border border-[#ebebf2] overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
        >
          <div className="px-4 py-3 border-b border-[#ebebf2] flex items-center gap-2">
            <Building2 className="w-4 h-4 text-yamaha-700" />
            <h3 className="text-[13px] font-semibold text-[#1a1b22]">Location Performance</h3>
            <span className="text-[10.5px] text-[#9ca0ad] ml-1">Click any number to view</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-[#faf8ff] text-[10px] font-bold uppercase tracking-[0.08em] text-[#a4a6b3]">
                <tr>
                  <th className="px-3 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-center">New</th>
                  <th className="px-3 py-2 text-center">Ordered</th>
                  <th className="px-3 py-2 text-center">Pending Pickup</th>
                  <th className="px-3 py-2 text-center">Delayed</th>
                  <th className="px-3 py-2 text-center">Follow-up</th>
                  <th className="px-3 py-2 text-center">SLA Breached</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f0f7]">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-[#9ca0ad]">Loading…</td>
                  </tr>
                )}
                {!loading && byLoc.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-[#9ca0ad]">
                      No accessible locations.
                    </td>
                  </tr>
                )}
                {byLoc.rows.map((r) => {
                  const h = HEALTH_STYLE[r.health] || HEALTH_STYLE.green;
                  const cell = (metric) => {
                    const { cls, txt } = num(r[metric], metric);
                    const clickable = r[metric] > 0 && WORKBENCH_FILTER[metric];
                    return (
                      <td
                        className={`px-3 py-2 text-center ${clickable ? "cursor-pointer hover:bg-[#faf8ff]" : ""}`}
                        onClick={clickable ? () => jumpTo(r.location_code, metric) : undefined}
                        data-testid={`branch-${r.location_code}-${metric}`}
                      >
                        <span className={cls}>{txt}</span>
                      </td>
                    );
                  };
                  return (
                    <tr key={r.location_code} className="hover:bg-[#fbfafd]">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`}></span>
                          <div className="flex flex-col">
                            <span className="text-[12.5px] font-semibold text-[#1a1b22] leading-tight">{r.location_name}</span>
                            <span className="text-[10px] text-[#9ca0ad] font-mono leading-tight">{r.location_code}</span>
                          </div>
                        </div>
                      </td>
                      {cell("new_orders")}
                      {cell("ordered")}
                      {cell("pending_pickup")}
                      {cell("delayed")}
                      {cell("customer_not_responding")}
                      {cell("sla_breached")}
                      <td className="px-3 py-2 text-right">
                        <button
                          data-testid={`branch-view-${r.location_code}`}
                          onClick={() => navigate(`/workbench?location=${r.location_code}`)}
                          className="text-[11px] text-yamaha-700 font-semibold hover:underline"
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                {byLoc.rows.length > 0 && (
                  <tr className="bg-[#faf8ff] font-semibold">
                    <td className="px-3 py-2 text-[11.5px] uppercase tracking-wider text-[#747684]">Total</td>
                    <td className="px-3 py-2 text-center">{totalRow.new_orders}</td>
                    <td className="px-3 py-2 text-center">{totalRow.ordered}</td>
                    <td className="px-3 py-2 text-center">{totalRow.pending_pickup}</td>
                    <td className="px-3 py-2 text-center text-rose-600">{totalRow.delayed}</td>
                    <td className="px-3 py-2 text-center text-purple-600">{totalRow.customer_not_responding}</td>
                    <td className="px-3 py-2 text-center text-orange-600">{totalRow.sla_breached}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Today's Attention */}
        <div
          data-testid="todays-attention"
          className="bg-white rounded-xl border border-[#ebebf2] overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
        >
          <div className="px-4 py-3 border-b border-[#ebebf2] flex items-center gap-2">
            <Siren className="w-4 h-4 text-rose-500" />
            <h3 className="text-[13px] font-semibold text-[#1a1b22]">Today's Attention</h3>
          </div>
          <ul className="divide-y divide-[#f1f0f7]">
            {attention.slice(0, 5).map((r) => {
              const h = HEALTH_STYLE[r.health];
              const hasIssue = r.score > 0;
              return (
                <li
                  key={r.location_code}
                  className={`px-4 py-3 flex items-start gap-3 ${hasIssue ? "cursor-pointer hover:bg-[#faf8ff]" : ""}`}
                  onClick={hasIssue ? () => navigate(`/workbench?location=${r.location_code}`) : undefined}
                  data-testid={`attention-${r.location_code}`}
                >
                  <span className={`w-2 h-2 rounded-full mt-1.5 ${h.dot}`}></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#1a1b22] leading-tight">
                      {r.location_name}{" "}
                      <span className="text-[10px] font-normal text-[#9ca0ad] font-mono">({r.location_code})</span>
                    </div>
                    <div className={`text-[11.5px] mt-0.5 ${hasIssue ? "text-[#444653]" : "text-emerald-600"}`}>
                      {hasIssue ? r.reason : "No critical issues"}
                    </div>
                  </div>
                  {hasIssue && <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-1" />}
                  {!hasIssue && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-1" />}
                </li>
              );
            })}
            {attention.length === 0 && !loading && (
              <li className="p-6 text-center text-[12px] text-[#9ca0ad]">No locations to show.</li>
            )}
          </ul>
        </div>
      </div>

      {/* --- 3) Location-wise breakdown widgets ----------------------------- */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ByLocationWidget
          title="Pending Pickup by Location"
          rows={byLoc.rows}
          metric="pending_pickup"
          dotClass="bg-emerald-500"
          onJump={jumpTo}
          testid="widget-pending-pickup-loc"
        />
        <ByLocationWidget
          title="Delayed by Location"
          rows={byLoc.rows}
          metric="delayed"
          dotClass="bg-rose-500"
          onJump={jumpTo}
          testid="widget-delayed-loc"
        />
        <ByLocationWidget
          title="SLA Breached by Location"
          rows={byLoc.rows}
          metric="sla_breached"
          dotClass="bg-orange-500"
          onJump={jumpTo}
          testid="widget-sla-loc"
        />
        <ByLocationWidget
          title="Customer Not Responding"
          rows={byLoc.rows}
          metric="customer_not_responding"
          dotClass="bg-purple-500"
          onJump={jumpTo}
          testid="widget-cnr-loc"
        />
      </div>

      {/* --- 4) Oldest cards ----------------------------------------------- */}
      <div className="grid md:grid-cols-2 gap-4">
        <OldestCard
          title="Oldest Pending Pickup"
          icon={Clock}
          data={byLoc.oldest_pending_pickup}
          locationName={locationName}
          navigate={navigate}
          accent="emerald"
          testid="oldest-pending-pickup"
        />
        <OldestCard
          title="Oldest Delayed Order"
          icon={AlertTriangle}
          data={byLoc.oldest_delayed}
          locationName={locationName}
          navigate={navigate}
          accent="rose"
          testid="oldest-delayed"
        />
      </div>

      {/* FAB retained */}
      {canAddSpares && (
      <button
        data-testid="fab-create"
        onClick={() => navigate("/create")}
        className="fixed bottom-24 right-10 bg-yamaha-700 hover:bg-yamaha-800 text-white pl-4 pr-5 py-2.5 rounded-full shadow-[0_8px_24px_rgba(0,51,153,0.35)] flex items-center gap-2 transition-all hover:scale-[1.03] z-40"
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
        <span className="text-[13px] font-semibold tracking-wide">Create Parts Request</span>
      </button>
      )}
    </div>
  );
};

// ------------ ByLocation compact list widget --------------------------------
const ByLocationWidget = ({ title, rows, metric, dotClass, onJump, testid }) => {
  const dot = dotClass;
  const sorted = useMemo(() => {
    return [...(rows || [])].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  }, [rows, metric]);
  const total = sorted.reduce((s, r) => s + (r[metric] || 0), 0);
  const showAll = () => {
    const wb = WORKBENCH_FILTER[metric];
    if (wb) window.location.href = `/workbench?filter=${wb}`;
  };
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-xl border border-[#ebebf2] overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    >
      <div className="px-4 py-3 border-b border-[#ebebf2] flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>
        <h3 className="text-[12.5px] font-semibold text-[#1a1b22] flex-1 leading-tight">{title}</h3>
        <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded-full bg-[#f4f3fc] text-[#444653]">{total}</span>
      </div>
      <ul className="divide-y divide-[#f1f0f7]">
        {sorted.map((r) => (
          <li
            key={r.location_code}
            className={`px-4 py-2 flex items-center justify-between text-[12px] ${
              r[metric] > 0 ? "cursor-pointer hover:bg-[#faf8ff]" : ""
            }`}
            onClick={r[metric] > 0 ? () => onJump(r.location_code, metric) : undefined}
            data-testid={`${testid}-row-${r.location_code}`}
          >
            <span className="text-[#444653]">{r.location_name} <span className="text-[10px] text-[#9ca0ad] font-mono">({r.location_code})</span></span>
            <span className={`font-semibold ${r[metric] > 0 ? "text-[#1a1b22]" : "text-[#9ca0ad]"}`}>
              {r[metric] || 0}
            </span>
          </li>
        ))}
      </ul>
      {total > 0 && (
        <button
          onClick={showAll}
          className="w-full text-[11px] text-yamaha-700 font-semibold py-2 border-t border-[#f1f0f7] hover:bg-[#faf8ff]"
        >
          View all →
        </button>
      )}
    </div>
  );
};

// ------------ Oldest card ---------------------------------------------------
const ACCENT_OLDEST = {
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700" },
};

const OldestCard = ({ title, icon: Icon, data, locationName, navigate, accent, testid }) => {
  const a = ACCENT_OLDEST[accent];
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-xl border border-[#ebebf2] p-4 flex items-center gap-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    >
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${a.bg} ${a.text} shrink-0`}>
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#9ca0ad]">{title}</div>
        {data ? (
          <div className="mt-1">
            <div className="text-[13px] font-semibold text-[#1a1b22] flex items-center gap-2">
              {locationName(data.location_code)}
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${a.bg} ${a.text}`}>
                {data.days} Days
              </span>
            </div>
            <div className="text-[11.5px] text-[#747684] mt-0.5 truncate">
              {data.customer_name} · {data.model_name || "—"} · {data.order_number || ""}
            </div>
          </div>
        ) : (
          <div className="mt-1 text-[12px] text-[#9ca0ad]">Nothing older to flag — good going!</div>
        )}
      </div>
      {data && (
        <button
          onClick={() => navigate(`/workbench?order=${data.id}`)}
          className="text-[11px] text-yamaha-700 font-semibold hover:underline shrink-0"
        >
          Open →
        </button>
      )}
    </div>
  );
};

export default Dashboard;
